import { useState } from 'react';
import { Button, Divider, Group, Modal, SegmentedControl, Stack, Text } from '@mantine/core';
import { resolveCoinFlip, sideOfSlot, type CoinFlipInput, type Round, type SideIndex } from '@flow/core';
import { run, useUseCases } from '../../app/services';
import { Term } from '../knowledge/KnowledgeDrawer';

/**
 * Guided coin flip: the winner chooses a side OR a speaking position; the loser chooses the other.
 * Also offers "just set it" for when sides are posted on the pairing.
 */
export const CoinFlipModal = ({ round, opened, onClose }: { round: Round; opened: boolean; onClose: () => void }) => {
  const uc = useUseCases();
  const [s0, s1] = round.format.sideLabels;
  const [winner, setWinner] = useState<'A' | 'B'>('A');
  const [choice, setChoice] = useState<'side' | 'order'>('side');
  const [winnerPick, setWinnerPick] = useState<string>('0');
  const [loserPick, setLoserPick] = useState<string>('first');
  const loser: 'A' | 'B' = winner === 'A' ? 'B' : 'A';
  const code = (t: 'A' | 'B') => (t === 'A' ? round.teamA.code : round.teamB.code);

  const flip: CoinFlipInput =
    choice === 'side'
      ? { winner, winnerChose: choice, sideTakenBy: { team: winner, side: Number(winnerPick) as SideIndex }, orderTakenBy: { team: loser, slot: (loserPick === 'first' ? 'first' : 'second') as 'first' | 'second' } }
      : { winner, winnerChose: choice, sideTakenBy: { team: loser, side: Number(loserPick) as SideIndex }, orderTakenBy: { team: winner, slot: (winnerPick === 'first' ? 'first' : 'second') as 'first' | 'second' } };

  const valid = choice === 'side' ? ['0', '1'].includes(winnerPick) && ['first', 'second'].includes(loserPick) : ['first', 'second'].includes(winnerPick) && ['0', '1'].includes(loserPick);
  const preview = valid ? resolveCoinFlip(flip) : null;
  const describe = (p: { teamASide: SideIndex; firstSide: SideIndex }) => {
    const aLabel = round.format.sideLabels[p.teamASide];
    const bLabel = round.format.sideLabels[(1 - p.teamASide) as SideIndex];
    const first = sideOfSlot({ firstSide: p.firstSide, format: round.format }, 'first');
    return `${code('A')} = ${aLabel}, ${code('B')} = ${bLabel}. ${first !== null ? round.format.sideLabels[first] : '?'} speaks first.`;
  };

  const sideData = [{ value: '0', label: s0 }, { value: '1', label: s1 }];

  // LD, Policy, Parli: no flip. Sides come from the pairing, and side 0 (Aff/Gov) always speaks first.
  if (round.format.order === 'fixed') {
    return (
      <Modal opened={opened} onClose={onClose} title={<Text fw={600}>Sides</Text>} size="sm">
        <Stack gap="sm">
          <Text size="sm">Which side is {code('A')} on? (It's on the pairing.) {s0} speaks first.</Text>
          <Group grow>
            {([0, 1] as SideIndex[]).map((aSide) => (
              <Button key={aSide} variant="light" data-testid={`sides-a-${aSide}`} onClick={async () => { if (await run(uc.setSides(round.id, aSide, 0))) onClose(); }}>
                {code('A')} {round.format.sideLabels[aSide]} · {code('B')} {round.format.sideLabels[(1 - aSide) as SideIndex]}
              </Button>
            ))}
          </Group>
        </Stack>
      </Modal>
    );
  }
  const orderData = [{ value: 'first', label: 'Speak first' }, { value: 'second', label: 'Speak second' }];

  return (
    <Modal opened={opened} onClose={onClose} title={<Text fw={600}><Term q="Coin flip">Coin flip</Term></Text>} size="md">
      <Stack gap="sm">
        <Text size="sm">Who won the flip?</Text>
        <SegmentedControl fullWidth value={winner} onChange={(v) => setWinner(v as 'A' | 'B')} data={[{ value: 'A', label: code('A') }, { value: 'B', label: code('B') }]} />
        <Text size="sm">{code(winner)} chose…</Text>
        <SegmentedControl fullWidth value={choice} onChange={(v) => { setChoice(v as 'side' | 'order'); setWinnerPick(v === 'side' ? '0' : 'first'); setLoserPick(v === 'side' ? 'first' : '0'); }} data={[{ value: 'side', label: 'a side' }, { value: 'order', label: 'speaking order' }]} />
        <Text size="sm">{code(winner)} took:</Text>
        <SegmentedControl fullWidth value={winnerPick} onChange={setWinnerPick} data={choice === 'side' ? sideData : orderData} />
        <Text size="sm">Then {code(loser)} took:</Text>
        <SegmentedControl fullWidth value={loserPick} onChange={setLoserPick} data={choice === 'side' ? orderData : sideData} />
        {preview && <Text size="sm" fw={600} data-testid="flip-preview">{describe(preview)}</Text>}
        <Group justify="flex-end">
          <Button variant="filled" disabled={!valid} onClick={async () => { if (await run(uc.recordCoinFlip(round.id, flip))) onClose(); }}>Save</Button>
        </Group>
        <Divider label="or set directly (sides posted on the pairing)" />
        <Group grow>
          {([0, 1] as SideIndex[]).map((aSide) =>
            ([0, 1] as SideIndex[]).map((first) => (
              <Button key={`${aSide}${first}`} size="xs" variant="light" onClick={async () => { if (await run(uc.setSides(round.id, aSide, first))) onClose(); }}>
                {code('A')} {round.format.sideLabels[aSide]}, {round.format.sideLabels[first]} 1st
              </Button>
            )),
          )}
        </Group>
      </Stack>
    </Modal>
  );
};
